import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveSession, loadSession } from '../utils/storage.ts';

interface LandingPageProps {}

const LandingPage: React.FC<LandingPageProps> = () => {
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const session = await loadSession();
      if (session && session.currentStep === 'questions') {
        navigate('/test', { replace: true });
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto px-4 py-16">
        
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Тест «Адаптивная эмпатия: Фаза 2»
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Исследование динамики эмпатии в контексте психологических защит, 
            диссоциации и детских травм
          </p>
        </div>

        {/* Main Info Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          
          {/* About Test */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-4xl mb-4">🧩</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">О тесте</h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Тест измеряет не просто эмпатию или психопатию, а их динамику в контексте 
              подавления, диссоциации и детских травм. Цель — выявить, сохраняется ли 
              способность к эмпатии при высоких уровнях психологической защиты.
            </p>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center">
                <span className="text-green-600 mr-2">✓</span>
                <span>110 вопросов (5-7 минут)</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-600 mr-2">✓</span>
                <span>Анонимное прохождение</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-600 mr-2">✓</span>
                <span>Возможность сохранить и продолжить</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-600 mr-2">✓</span>
                <span>Детальный анализ результатов</span>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-4xl mb-4">📊</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Что вы получите</h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              По результатам теста вы получите персональный профиль с определением 
              вашего типа эмпатии и рекомендациями для дальнейшей работы.
            </p>

            <div className="space-y-4">
              <div className="flex items-start">
                <span className="text-blue-600 text-2xl mr-3">🧊</span>
                <div>
                  <div className="font-medium">Замороженные</div>
                  <div className="text-sm text-gray-600">Высокая защита, формальная эмпатия</div>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-purple-600 text-2xl mr-3">🧠</span>
                <div>
                  <div className="font-medium">Прагматики</div>
                  <div className="text-sm text-gray-600">Высокая эмпатия + психопатические черты</div>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-green-600 text-2xl mr-3">🧩</span>
                <div>
                  <div className="font-medium">Адаптивные</div>
                  <div className="text-sm text-gray-600">Баланс эмпатии и защитных механизмов</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Relevance Section */}
        <div className="bg-indigo-50 rounded-xl shadow-inner p-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800">Актуальность</h2>
            <p className="text-gray-600 mt-2">На чем основано наше исследование</p>
          </div>
          
          <div className="max-w-4xl mx-auto bg-white rounded-lg p-6 text-gray-700 text-sm leading-relaxed">
            <p className="mb-4">
              Исследование опирается на актуальные научные данные 2020–2024 гг. в области социальной психологии и нейробиологии:
            </p>
            <ul className="space-y-4">
              <li>
                <strong>Избирательная эмпатия:</strong> Показано, что эмпатия не является автоматической реакцией, а включается выборочно — в зависимости от нашей моральной оценки другого человека (J. Zaki, P. Bloom, C. D. Cameron).
              </li>
              <li>
                <strong>«Тёмные эмпаты»:</strong> Активно изучается феномен людей, сочетающих высокую когнитивную эмпатию (понимание чужих эмоций) с психопатическими чертами, что позволяет им эффективно манипулировать окружающими.
              </li>
              <li>
                <strong>Эмпатия как ресурс:</strong> Работы D. Cameron и др. (2022) показывают, что проявление эмпатии — это затратное когнитивное усилие, которого мы можем сознательно избегать, если издержки кажутся слишком высокими.
              </li>
              <li>
                <strong>ACE и эмпатия:</strong> Установлена прямая связь между детским травматическим опытом (Adverse Childhood Experiences) и последующим снижением эмпатии, а также ростом агрессивного поведения, особенно в подростковых и криминальных выборках.
              </li>
            </ul>
          </div>
        </div>

        {/* Formula */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Интегральная метрика
          </h2>
          
          <div className="text-center mb-6">
            <div className="bg-white rounded-lg p-6 inline-block font-mono text-xl">
              R = (Эмпатия - S<sub>общ</sub>) × (1 - ψ)
            </div>
          </div>
          
          <div className="text-center text-gray-700">
            <p className="mb-2">
              <strong>Эмпатия</strong> — способность к эмоциональному отклику
            </p>
            <p className="mb-2">
              <strong>S<sub>общ</sub></strong> — защитные механизмы (подавление + диссоциация + алекситимия)
            </p>
            <p>
              <strong>ψ</strong> — нормированные психопатические черты
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <button
            data-testid="start-test-button"
            onClick={async () => { await saveSession({}); navigate('/test'); }}
            className="bg-blue-600 text-white font-bold py-4 px-8 rounded-lg text-xl hover:bg-blue-700 transition-colors shadow-lg"
          >
            Начать тест
          </button>
          <p className="text-gray-500 mt-4 text-sm">
            Время прохождения: 5-7 минут
          </p>
        </div>

        {/* Disclaimer */}
        <div className="mt-16 bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start">
            <div className="text-yellow-600 text-2xl mr-3">⚠️</div>
            <div>
              <h3 className="font-semibold text-yellow-800 mb-2">Важное уведомление</h3>
              <p className="text-yellow-700 text-sm leading-relaxed">
                Результаты теста предназначены для исследовательских целей и самопознания. 
                Они не являются медицинским диагнозом и не заменяют консультацию 
                квалифицированного специалиста. При серьезных психологических проблемах 
                обратитесь к психологу или психотерапевту.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LandingPage;